/**
 * Arabic numerals → the spoken English words (and Chinese) for them.
 *
 * Why this exists: word clickability on both the student cards and the teacher's
 * smart review is gated on "is this token a key in the CDN vocab dictionary".
 * Number WORDS ("one", "twenty") are all in there, but digits are not — only 27
 * arbitrary ones happened to get uploaded ('3', '6', '10', '1628'…), so "I have 3
 * apples" was tappable while "I have 1 apple" was dead. Digits are infinite, so
 * they can't be enumerated into the dictionary; they get synthesised here instead,
 * reusing the number-word recordings that already exist.
 */

// Every word below is present in cdnVocabWords.json with real CDN audio — that's
// the constraint this module is written against. Don't emit a word not on this list.
const ONES = [
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen',
];
const TENS = [
  '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety',
];
const SCALES = [
  { value: 1e9, word: 'billion' },
  { value: 1e6, word: 'million' },
  { value: 1e3, word: 'thousand' },
];

// Above this the reading stops being useful and the token is almost certainly not
// a quantity (phone numbers, IDs). Such tokens stay unclickable rather than
// producing a 20-word audio sequence.
const MAX_VALUE = 1e12;

/**
 * The integer value of a token, or null when it isn't a plain number.
 *
 * Only all-digit tokens qualify. Callers pass `cleanWord` output, which has
 * already stripped `.,!?;:()[]{}` — so "10,000" arrives as "10000" and "2." as
 * "2". Tokens that still carry other characters ("$30", "11-year-old", "20%",
 * "24th") are deliberately rejected: they are not numbers, they only contain one.
 */
export function parseNumericToken(token) {
  if (!token || !/^\d+$/.test(token)) return null;
  const value = Number(token);
  if (!Number.isFinite(value) || value >= MAX_VALUE) return null;
  return value;
}

function wordsBelowThousand(n) {
  const out = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds > 0) out.push(ONES[hundreds], 'hundred');
  if (rest > 0) {
    if (rest < 20) {
      out.push(ONES[rest]);
    } else {
      out.push(TENS[Math.floor(rest / 10)]);
      const ones = rest % 10;
      if (ones > 0) out.push(ONES[ones]);
    }
  }
  return out;
}

/**
 * The spoken English words for a number, as an ordered array.
 *
 * Read as a cardinal quantity, so 1860 is "one thousand eight hundred sixty"
 * rather than the year reading "eighteen sixty" — the token alone doesn't say
 * which it is, and the quantity reading is the one that's always defensible.
 *
 * @param {number} n non-negative integer
 * @returns {string[]}
 */
export function numberToEnglishWords(n) {
  if (!Number.isInteger(n) || n < 0 || n >= MAX_VALUE) return [];
  if (n < 20) return [ONES[n]];

  const out = [];
  let remaining = n;
  for (const { value, word } of SCALES) {
    const count = Math.floor(remaining / value);
    if (count > 0) {
      out.push(...numberToEnglishWords(count), word);
      remaining %= value;
    }
  }
  if (remaining > 0) out.push(...wordsBelowThousand(remaining));
  return out;
}

const CN_DIGITS = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
const CN_UNITS = ['', '十', '百', '千'];

/** Chinese numeral for 0 <= n < 10000. */
function chineseBelowTenThousand(n) {
  if (n === 0) return '';
  let out = '';
  let zeroPending = false;
  const digits = String(n).split('').map(Number);
  digits.forEach((d, i) => {
    const unit = CN_UNITS[digits.length - 1 - i];
    if (d === 0) {
      zeroPending = out.length > 0;
      return;
    }
    if (zeroPending) {
      out += CN_DIGITS[0];
      zeroPending = false;
    }
    // 10-19 read as 十/十一, not 一十/一十一.
    out += (d === 1 && unit === '十' && out.length === 0) ? '十' : CN_DIGITS[d] + unit;
  });
  return out;
}

/**
 * Chinese numeral for a number, or null when it's out of the supported range.
 * Covers 0 to 99,999,999 (up to 万), which is every number these courses use.
 *
 * @param {number} n
 * @returns {string|null}
 */
export function numberToChinese(n) {
  if (!Number.isInteger(n) || n < 0) return null;
  if (n === 0) return CN_DIGITS[0];
  if (n >= 1e8) return null;

  const wan = Math.floor(n / 10000);
  const rest = n % 10000;
  if (wan === 0) return chineseBelowTenThousand(rest);

  let out = chineseBelowTenThousand(wan) + '万';
  if (rest === 0) return out;
  // A gap below 1000 in the lower half needs a 零 (100005 → 十万零五).
  if (rest < 1000) out += CN_DIGITS[0];
  return out + chineseBelowTenThousand(rest);
}
